import React from 'react';
import { useContent } from '../../context/ContentContext';
import { resolveMediaUrl } from '../../utils/api';
import { motion } from 'motion/react';
import { Sparkles, Instagram, Users } from 'lucide-react';

export const BaristasSection: React.FC = () => {
  const { data } = useContent();
  const baristasContent = data.baristasContent || {
    title: 'MEET OUR BARISTAS',
    subtitle: 'THE CRAFTSMEN, ROASTERS & CREATIVE BREWERS',
    badge: '06 — THE ARTISAN TEAM',
    description:
      'Di balik setiap tegukan kopi Leton, ada dedikasi, keahlian teknik seduh, dan senyuman hangat dari tim barista kami yang siap menemani hari dan obrolanmu.',
  };

  const baristas = [...(data.baristas || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );

  return (
    <section
      id="baristas"
      className="relative w-full bg-[#FFFFFF] py-16 sm:py-24 border-t border-[#E2E8F0] overflow-hidden"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F8FAFC] border border-[#E2E8F0] text-[#0056D2] text-xs font-mono tracking-widest uppercase mb-4 shadow-xs"
          >
            <Users className="w-3.5 h-3.5 text-[#0056D2]" />
            <span>{baristasContent.badge || '06 — THE ARTISAN TEAM'}</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-display font-black text-3xl sm:text-5xl tracking-tight text-[#0A192F] uppercase"
          >
            {baristasContent.title || 'MEET OUR BARISTAS'}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="font-display font-bold text-xs sm:text-sm text-[#0056D2] tracking-wider uppercase mt-2"
          >
            {baristasContent.subtitle || 'THE CRAFTSMEN, ROASTERS & CREATIVE BREWERS'}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-[#64748B] text-sm sm:text-base mt-4 leading-relaxed max-w-2xl mx-auto"
          >
            {baristasContent.description}
          </motion.p>
        </div>

        {/* Baristas Grid */}
        {baristas.length === 0 ? (
          <div className="text-center py-16 p-8 rounded-3xl bg-[#F8FAFC] border border-[#E2E8F0] text-[#64748B]">
            <Users className="w-10 h-10 mx-auto text-[#0056D2] mb-3" />
            <p className="font-display font-bold text-base text-[#0A192F] uppercase">
              Belum ada data barista
            </p>
            <p className="text-xs text-[#64748B] mt-1">
              Tambahkan profil barista melalui CMS Admin Dashboard.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {baristas.map((barista, index) => (
              <motion.div
                key={barista.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group flex flex-col cursor-default bg-white p-3.5 rounded-3xl border border-[#E2E8F0] hover:border-[#0056D2]/50 shadow-[0_8px_24px_rgba(10,25,47,0.05)] transition-all"
              >
                {/* Photo Container */}
                <div className="relative aspect-[3/4] w-full rounded-2xl overflow-hidden bg-[#F8FAFC] border border-[#E2E8F0] shrink-0">
                  {barista.image ? (
                    <img
                      src={resolveMediaUrl(barista.image)}
                      alt={barista.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        const target = e.currentTarget;
                        if (target.src !== '/assets/leton-coffee-beans.jpg' && !target.src.endsWith('/assets/leton-coffee-beans.jpg')) {
                          target.src = '/assets/leton-coffee-beans.jpg';
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-[#64748B]">
                      <Users className="w-12 h-12 mb-2 stroke-1" />
                      <span className="text-xs font-mono">Foto Barista</span>
                    </div>
                  )}

                  {/* Subtle Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/60 via-transparent to-transparent opacity-60 pointer-events-none" />

                  {/* Instagram Button */}
                  {barista.instagram && (
                    <div className="absolute top-3.5 right-3.5 z-10">
                      <a
                        href={barista.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Instagram ${barista.name}`}
                        className="p-2 rounded-full bg-[#0A192F]/80 hover:bg-[#0056D2] text-white border border-white/20 backdrop-blur-md transition-all transform hover:scale-110 flex items-center justify-center shadow-lg"
                      >
                        <Instagram className="w-3.5 h-3.5 text-white" />
                      </a>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="pt-3.5 pb-1 px-1 flex flex-col">
                  <h3 className="font-display font-black text-lg sm:text-xl text-[#0A192F] tracking-tight uppercase leading-tight">
                    {barista.name}
                  </h3>
                  <p className="mt-1 text-xs font-bold tracking-wider text-[#0056D2] uppercase">
                    {barista.role}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Culture Quote Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-12 sm:mt-16 p-7 sm:p-10 rounded-3xl bg-[#0A192F] border border-[#0A192F] text-center relative overflow-hidden shadow-xl"
        >
          <div className="flex items-center justify-center gap-2 text-blue-400 font-mono text-xs font-bold uppercase tracking-widest mb-2">
            <Sparkles className="w-4 h-4 text-blue-400" />
            <span>THE LETON STANDARD</span>
          </div>
          <p className="font-display font-black text-lg sm:text-2xl text-white uppercase max-w-2xl mx-auto tracking-tight">
            &ldquo;Setiap cangkir adalah kolaborasi rasa, seni ekstraksi, dan percakapan tulus.&rdquo;
          </p>
        </motion.div>
      </div>
    </section>
  );
};
